import React, { useEffect, useState, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

import 'chartjs-plugin-annotation';
import { useSelector } from 'react-redux';
import PopupDetailviewmetrics from './PopupDetailviewmetrics';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import Modal from 'react-modal';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {Paper,useTheme} from '@mui/material';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import PropTypes from "prop-types";

const Detailstoriesmetricstalent = ({initialPodIds,selectedSprint}) => {
  const [metrics, setMetrics] = useState([]);
  const [open_stories, setOpenStories] = useState([]);
  const [closed_stories, setClosedStories] = useState([]);
  const [unassigned_stories, setUnassignedStories] = useState([]);
  const { user } = useSelector(state => state.auth);
  const chartRef = useRef(null);
  const [popupData, setPopupData] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [selectedReporter, setSelectedReporter] = useState("");
  const [issues, setIssues] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const token = useAuthToken();
  const intl = useIntl();
  const theme = useTheme();

  useEffect(() => {
    Chart.register(...registerables);
    fetchMetrics();
  }, [token, user.data[0].type, initialPodIds, selectedSprint]);

  const fetchMetrics = async () => {
    try {
      if (!token) {
        return;
      }
      const response = await fetch(process.env.REACT_APP_TALENT_JIRASTORIESMETRICSTALENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          c_id: user.data[0].c_id,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || '',
        }),
      });

      const data = await response.json();
      const parsedData = JSON.parse(data.body);
      
      // Filter unassigned stories from open and closed stories
      const unassignedOpenStories = parsedData.open_stories?.filter(story => story.Reporter === "Unassigned") || [];
      const unassignedClosedStories = parsedData.closed_stories?.filter(story => story.Reporter === "Unassigned") || [];
      const allUnassignedStories = [...unassignedOpenStories, ...unassignedClosedStories];
      
      setTableData([...parsedData.open_stories, ...parsedData.closed_stories]);

      if (Array.isArray(parsedData.reporters)) {
        setMetrics(parsedData.reporters);
        setOpenStories(parsedData.open_stories);
        setClosedStories(parsedData.closed_stories);
        setUnassignedStories(allUnassignedStories);
        setIssues({
          open_stories: parsedData.open_stories,
          closed_stories: parsedData.closed_stories,
          unassigned_stories: allUnassignedStories,
        });
      } else {
        console.log('Metrics data is empty or not in the expected format');
        setMetrics([]);
        setOpenStories([]);
        setClosedStories([]);
        setUnassignedStories([]);
        setIssues([]);
      }
    } catch (error) {
      console.log('Error fetching metrics:', error);
      setMetrics([]);
      setOpenStories([]);
      setClosedStories([]);
      setUnassignedStories([]);
      setIssues([]);
    }
  };

  // Function to handle changes in selected reporter
  const handleReporterChange = (e) => {
    setSelectedReporter(e.target.value);
  };

  // Function to calculate chart data based on current state
  const calculateChartData = () => {
    if (selectedReporter) {
      if (selectedReporter === "Unassigned") {
        // Show only unassigned stories
        const allUnassignedStories = [...open_stories, ...closed_stories].filter(story => story.Reporter === "Unassigned");
        return [0, 0, allUnassignedStories.length];
      } else {
        // Show specific reporter's stories
        const selectedReporterData = metrics.find((item) => item.Reporter === selectedReporter);
        if (selectedReporterData) {
          return [selectedReporterData.open_user_stories, selectedReporterData.completed_user_stories, 0];
        }
        return [0, 0, 0];
      }
    } else {
      // Show all stories divided into three categories
      const assignedOpenStories = open_stories.filter(story => story.Reporter !== "Unassigned");
      const assignedClosedStories = closed_stories.filter(story => story.Reporter !== "Unassigned");
      const allUnassignedStories = [...open_stories, ...closed_stories].filter(story => story.Reporter === "Unassigned");
      
      return [assignedOpenStories.length, assignedClosedStories.length, allUnassignedStories.length];
    }
  };

  // Create/update chart whenever data changes
  useEffect(() => {
    if (open_stories.length > 0 || closed_stories.length > 0 || metrics.length > 0) {
      createChart();
    }
  }, [metrics, selectedReporter, open_stories, closed_stories]);

  const createChart = () => {
    const ctx = document.getElementById('completedChart');
    if (!ctx) return;

    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const chartData = calculateChartData();
    
    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Open Stories', 'Closed Stories', 'Unassigned Stories'],
        datasets: [{
          label: 'Stories',
          data: chartData,
          backgroundColor: ['lightgreen', 'red', 'orange'],
          borderWidth: 1,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          annotation: {},
          legend: {
            display: false,
          },
        },
        layout: {
          padding: {
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
          },
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            handleChartClick(elements[0].index);
          }
        },
      },
    });
  };

  // Handle chart segment clicks
  const handleChartClick = (segmentIndex) => {
    const labels = ['Open Stories', 'Closed Stories', 'Unassigned Stories'];
    const clickedLabel = labels[segmentIndex];
    
    let filteredStories = [];
    let popupTitle = '';

    switch (segmentIndex) {
      case 0: // Open Stories
        if (selectedReporter && selectedReporter !== "Unassigned") {
          filteredStories = open_stories.filter(story => story.Reporter === selectedReporter);
          popupTitle = `${selectedReporter} - Open Stories`;
        } else {
          filteredStories = open_stories.filter(story => story.Reporter !== "Unassigned");
          popupTitle = 'All Open Stories';
        }
        break;
      case 1: // Closed Stories  
        if (selectedReporter && selectedReporter !== "Unassigned") {
          filteredStories = closed_stories.filter(story => story.Reporter === selectedReporter);
          popupTitle = `${selectedReporter} - Closed Stories`;
        } else {
          filteredStories = closed_stories.filter(story => story.Reporter !== "Unassigned");
          popupTitle = 'All Closed Stories';
        }
        break;
      case 2: // Unassigned Stories
        filteredStories = [...open_stories, ...closed_stories].filter(story => story.Reporter === "Unassigned");
        popupTitle = 'Unassigned Stories';
        break;
    }

    setTableData(filteredStories);
    setPopupData({
      Reporter: popupTitle,
      stories: filteredStories,
    });
    setIsPopupVisible(true);
    setModalIsOpen(true);
  };

  // Function to open modal
  const openModal = () => {
    setModalIsOpen(true);
  };

  // Function to close modal
  const closeModal = () => {
    setModalIsOpen(false);
  };

  return (
    <div className="chart-container">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <h2 className="chart-heading">{intl.formatMessage({ id: 'stories' })}</h2>
        <br />
        <select   
          value={selectedReporter}
          onChange={handleReporterChange}
          style={{ 
            width: '12rem', 
            color: theme.palette.mode === 'dark' ? 'white' : 'black',      
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white', 
          }}
        >
          <option value="">{intl.formatMessage({id:'allReporters'})}</option>
          {metrics.map(item => (
            <option key={item.Reporter} value={item.Reporter}>
              {item.Reporter}
            </option>
          ))}
        </select>
        <br/>
        <div className="chart-wrapper" style={{ width: "145px", height: "145px", margin: "auto" }}>
          {(metrics.length > 0 || open_stories.length > 0 || closed_stories.length > 0) ? (
            <canvas id="completedChart"></canvas>
          ) : (
            <p>{intl.formatMessage({ id: 'noDataAvailable' })}</p>
          )}
        </div>
      </div>
      
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Stories Summary Modal"
        style={{
          content: {
            width: '50%',
            maxWidth: '800px',
            margin: 'auto',
            marginTop: '5rem',
          },
        }}
      >
        <TableContainer component={Paper}>
          <Table component={Paper} sx={{ maxWidth: 900, fontFamily: 'Poppins,Medium', borderRadius: '10px' }} aria-label="simple table">
            <TableHead
              sx={{
                backgroundColor: '#00B4D8',
                '& td, & th': {
                  fontFamily: 'Poppins,Medium',
                  fontSize: '15px',
                  color: 'white',
                  fontWeight: '600',
                },
              }}
            >
              <TableRow>
                <TableCell align="right">{intl.formatMessage({ id: 'reporter' })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: 'issueKey' })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: 'issueSummary' })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: 'issueStatus' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((story, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0, fontFamily: 'Poppins,Medium', fontSize: '15px' },
                    '&:nth-child(even)': {
                      backgroundColor: '#90E0EF',
                    },
                    '&:nth-child(odd)': {
                      backgroundColor: '#caf0f8',
                    },
                    '& td, & th': {
                      fontFamily: 'Poppins,Medium',
                      fontSize: '15px',
                      color: 'black',
                    },
                  }}
                >
                  <TableCell align="right">{story.Reporter || 'Unassigned'}</TableCell>
                  <TableCell align="right">{story.issue_key}</TableCell>
                  <TableCell align="right">{story.issue_summary}</TableCell>
                  <TableCell align="right">{story.issue_status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <button onClick={closeModal}>Close</button>
      </Modal>
    </div>
  );
};

Detailstoriesmetricstalent.propTypes = {
  initialPodIds: PropTypes.array.isRequired,
};

export default Detailstoriesmetricstalent;